import { useState } from 'react'
import { Modal, Form, Input, Button, Message } from '@arco-design/web-react'
import { useTranslation } from 'react-i18next'
import { bindEmailApi, sendEmailVerificationApi } from '@/api/user'
import { getFormValidateErrorMessage } from '@/utils/helpers'

interface EmailBindModalProps {
  visible: boolean
  onClose: () => void
  onSuccess: () => Promise<void>
  initialEmail?: string
}

export default function EmailBindModal({ visible, onClose, onSuccess, initialEmail }: EmailBindModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [countdown, setCountdown] = useState(0)

  const sendCode = async () => {
    try {
      const email = form.getFieldValue('email')
      if (!email) {
        Message.warning(t('personal.emailModal.enterEmailFirst'))
        return
      }
      setSending(true)
      await sendEmailVerificationApi(email)
      Message.success(t('personal.emailModal.sendCode'))
      setCountdown(60)
      const timer = setInterval(() => {
        setCountdown((c) => {
          if (c <= 1) { clearInterval(timer); return 0 }
          return c - 1
        })
      }, 1000)
    } catch (err: unknown) {
      Message.error(getFormValidateErrorMessage(err))
    } finally {
      setSending(false)
    }
  }

  const submit = async () => {
    try {
      const values = await form.validate()
      setLoading(true)
      await bindEmailApi(values.email, values.code)
      Message.success(t('personal.emailModal.success'))
      onClose()
      await onSuccess()
    } catch (err: unknown) {
      Message.error(getFormValidateErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={t('personal.emailModal.title')}
      visible={visible}
      onCancel={onClose}
      onOk={() => void submit()}
      confirmLoading={loading}
      unmountOnExit
      afterOpen={() => form.setFieldsValue({ email: initialEmail || '', code: '' })}
    >
      <Form form={form} layout="vertical">
        <Form.Item field="email" label={t('personal.emailModal.email')} rules={[{ required: true, type: 'email' }]}>
          <Input />
        </Form.Item>
        <Form.Item field="code" label={t('personal.emailModal.code')} rules={[{ required: true }]}>
          <Input
            addAfter={
              <Button type="text" size="small" disabled={countdown > 0} loading={sending} onClick={() => void sendCode()}>
                {countdown > 0 ? `${countdown}s` : t('personal.emailModal.sendCode')}
              </Button>
            }
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
